import React, { Component } from 'react'
import { observable } from 'mobx'
import {observer} from 'mobx-react'
import _debug from 'debug'

import { IDrawablePassageGloss, Passage, PassageGloss, PassageVideo, Project } from '../../models3/ProjectModels'
import './Gloss.css'
import GlossView from './GlossView'
import GlossEditor from './GlossEditor'
import { displayError } from '../utils/Errors'
import { SearchButton, ZoomInGlossButton, ZoomOutGlossButton } from '../utils/Buttons'
import { t } from 'ttag'
import { GlossTextSearch } from './GlossTextSearch'
import { fmt } from '../utils/Fmt'
import { IRoot } from '../../models3/Root'


import { GlossInfo } from '../../models3/GlossInfo'
let log = _debug('sltt:GlossBar')

interface IGlossBar {
    rt: IRoot,
    w: number,
    setLexicalLink: (lexicalLink: string) => void,
    disallowGlossEdit?: boolean, // If true, do not show the gloss editor
    glossBarDisplay: IGlossBarDisplay,
}

interface IGlossBarDisplay {
    glossScale: number,
    setGlossScale: (scale: number) => void,
    passage: Passage | null,
    passageVideo: PassageVideo | null,
    project: Project,
    currentTime: number,
    iAmInterpreter: boolean,
    play: (startTime?: number, endPosition?: number, resetTime?: number) => void,
    stop: () => void,
    resetCurrentTime: (newTime: number) => void,
    glossInfo: GlossInfo,
}

class GlossBar extends Component<IGlossBar>  {
    drawables: IDrawablePassageGloss[] = []

    videoId = ''   // drawables were computed for this video/rev/glossScale
    videoRev = -1
    glossScale = -1

    constructor(props: IGlossBar) {
        super(props)

        // this.element = React.createRef()
        this.onDelete = this.onDelete.bind(this)
    }

    timeToX(time: number) {
        let { glossScale } = this.props.glossBarDisplay
        let { w } = this.props

        return 2 * time * glossScale + w/2 + 100
    }

    XToTime(x: number) {
        let { glossScale } = this.props.glossBarDisplay
        let { w } = this.props

        return (x - w / 2 - 100) / (2 * glossScale)
    }

    // When the passageVideo changes, update the list of drawable glosses.
    // Drawable glosses are those that are not covered by later patches.
    // The x values must be recomputed when the glossScale changes.
    getDrawables = () => {
        let { glossBarDisplay } = this.props
        let { passage, passageVideo, glossScale, glossInfo } = glossBarDisplay

        const glosses = glossInfo.visibleGlosses
        
        if (!passage || !passageVideo) return []
        
        const drawables: IDrawablePassageGloss[] = []
        this.videoId = passageVideo._id
        this.videoRev = passageVideo._rev
        this.glossScale = glossScale


        for (let i=0; i<glosses.length; ++i) {
            let gloss = glosses[i]
            let x = this.timeToX(gloss.time)
            let width = 100
            let duration = 0
            try {
                duration = gloss.durationByIndex(glosses, i)
                // Limit width to 2 seconds so that if the user only specifices a start time, the gloss play time will not go to the end of the video.
                duration = Math.min(duration, 2)

                // Calculate width in pixels based on duration and glossScale.
                // Four years ago I knew why this required a factor of 2, but I've forgotten. Sigh.
                width = 2 * glossScale * duration
            } catch (error) {
                log('###duration failed', fmt({text: gloss.text, time: gloss.time}))          
            }
            let time = gloss.time

            drawables.push({ x, duration, width, time, gloss })

            if (i > 0) {
                drawables[i - 1].width = drawables[i].x - drawables[i - 1].x
            }
        }

        return drawables
    }

    render() {
        let { rt, glossBarDisplay, setLexicalLink, disallowGlossEdit } = this.props
        let { passage, passageVideo, currentTime, iAmInterpreter,
            glossScale, setGlossScale, play, stop, resetCurrentTime, project, glossInfo } = glossBarDisplay
        
        const { visibleGlosses } = glossInfo // rerender when visibleGlosses changes
        // log('render', visibleGlosses.length)
        
        if (!passage || !passageVideo) return null


        const { editing } = glossInfo

        let { w } = this.props

        // access _rev forces redraw when anything about passage video changes
        let { _rev } = passageVideo

        // Drawables are glosses that are both visible on in the video and
        // fall within the time range shown in the current glossbar.
        // This depended on the current time and the gloss scale.
        // The horizontal position of each gloss is computed from the gloss time and the gloss scale.
        const drawables = this.getDrawables()
        this.drawables = drawables

        let { searchIsOpen } = rt.gtsp

        let xCurrent = this.timeToX(currentTime)
        
        let xMin = xCurrent - w / 2
        let xMax = xCurrent + w / 2 - 60
        
        let start = drawables.findIndex(dr => dr.x > xMin)
        if (start === -1) {
            start = drawables.length
        }

        let end = start
        for (; end<drawables.length && drawables[end].x < xMax; ++end) {}
        
        let _drawables = drawables.slice(start, end)

        let width = _drawables.length ? _drawables[0].x - xMin : 100
        let glossesAreaWidth = w - 27  // Leave room for scale adjuster buttons

        let editingEnabled = !passage.videoBeingCompressed && iAmInterpreter && !disallowGlossEdit
        let zoomInEnabled = editingEnabled && drawables.length > 0
        let zoomOutEnabled = editingEnabled && drawables.length > 0

        return (
            <div className="gloss-bar" >
                <div>
                    <div style={ {left: w/2 } } 
                        className="gloss-cursor">v</div>
                    <div className="gloss-glosses" style={{ width: glossesAreaWidth, display: 'inline-block', verticalAlign: '25px' }}>
                        <div style={{ width, display: 'inline-block' }} />
                        {_drawables.map((dr, i) => {
                            return (
                                <GlossView
                                    rt={rt}
                                    project={project}
                                    onClick={() => editingEnabled && this.setGlossIndex(start+i)}
                                    key={i} 
                                    gloss={dr.gloss} 
                                    width={dr.width}
                                    focused={start+i === glossInfo.glossIndex}
                                    last={i === drawables.length-1} />
                            )
                        })}
                    </div>
                    <div className="gloss-scale-container">
                        <div>
                            <SearchButton
                                enabled={true}
                                className="gloss-search-button"
                                tooltip={/* translator: important */ t`Search for text in glosses`}
                                onClick={() => rt.gtsp.searchIsOpen = true} />
                        </div>
                        <div>
                            <ZoomInGlossButton enabled={zoomInEnabled} onClick={() => {
                                setGlossScale(1.1 * glossScale)
                                this.getDrawables() }} />
                            <ZoomOutGlossButton enabled={zoomOutEnabled} onClick={() => {
                                setGlossScale(.9 * glossScale)
                                this.getDrawables() }} />
                        </div>
                    </div>
                </div>
                {editing && <GlossEditor
                    {...{ passage, passageVideo, play, stop, resetCurrentTime, drawables, onDelete: this.onDelete, onDone: this.onDone, project, glossInfo }} />}
                {searchIsOpen && 
                    <GlossTextSearch {...{
                        rt,
                        play,
                        drawables,
                        setSearchIsOpen: (searchIsOpen: boolean) => rt.gtsp.searchIsOpen = searchIsOpen, 
                    }} />}
            </div>
        )
    }

    setGlossIndex = (index: number) => { 
        let { glossBarDisplay } = this.props
        let { play, glossInfo } = glossBarDisplay

        glossInfo.setGlossIndex(index)

        let { time, duration } = this.drawables[index]
        log(`setGlossIndex index=${index}, time=${time.toFixed(1)}, duration=${duration.toFixed(1)}`)
        play(time, time + duration, time)
    }

    async onDelete() {
        let { glossBarDisplay } = this.props
        let { passage, glossInfo } = glossBarDisplay
        if (!passage) return

        const { passageGloss } = glossInfo
        if (!passageGloss) return

        let containingVideo = passageGloss.toVideo(passage)
        if (!containingVideo) return

        containingVideo.removeGloss(passageGloss._id).catch(displayError)
        
        glossInfo.closeEditor()
    }

    /** Close editor when done */
    onDone = () => {
        this.props.glossBarDisplay.glossInfo.closeEditor()
    }
}

export default observer(GlossBar)
